import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../ui/LoadingSpinner';

const DEFAULT_PERMISSIONS = {
  dashboard: false,
  shipments: false,
  clients: false,
  drivers: false,
  vehicles: false,
  accounting: false,
  quotation: false,
  vendors: false,
  users: false,
  settings: false,
};

const PERMISSION_ORDER = [
  'dashboard',
  'shipments',
  'clients',
  'drivers',
  'vehicles',
  'accounting',
  'quotation',
  'vendors',
  'users',
  'settings',
];

const ROLE_PERMISSIONS = {
  admin: {
    dashboard: true, shipments: true, clients: true, drivers: true,
    vehicles: true, accounting: true, quotation: true, vendors: true, users: true, settings: true,
  },
  operations: {
    dashboard: true, shipments: true, clients: true, drivers: true,
    vehicles: true, accounting: false, quotation: true, vendors: false, users: false, settings: false,
  },
  accounting: {
    dashboard: true, shipments: true, clients: true, drivers: false,
    vehicles: false, accounting: true, quotation: true, vendors: true, users: false, settings: false,
  },
  client: {
    dashboard: true, shipments: false, clients: false, drivers: false,
    vehicles: false, accounting: false, quotation: false, vendors: false, users: false, settings: false,
  },
};

const UserModal = ({
  isOpen,
  onClose,
  onSave,
  user = null,
  loading = false,
  companyId,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'operations',
    status: 'active',
    password: '',
    confirmPassword: '',
    permissions: { ...DEFAULT_PERMISSIONS, ...ROLE_PERMISSIONS.operations },
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        role: user.role || 'operations',
        status: user.status || 'active',
        password: '',
        confirmPassword: '',
        permissions: { ...DEFAULT_PERMISSIONS, ...(user.permissions || {}) },
      });
    } else {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        role: 'operations',
        status: 'active',
        password: '',
        confirmPassword: '',
        permissions: { ...DEFAULT_PERMISSIONS, ...ROLE_PERMISSIONS.operations },
      });
    }
    setErrors({});
  }, [user, isOpen]);

  // Auto-set permissions when role changes (but not when editing existing user on initial load)
  const [roleTouched, setRoleTouched] = useState(false);
  useEffect(() => {
    if (!roleTouched) return;
    if (ROLE_PERMISSIONS[formData.role]) {
      setFormData(prev => ({
        ...prev,
        permissions: { ...DEFAULT_PERMISSIONS, ...ROLE_PERMISSIONS[formData.role] },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.role]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = t('users.errors.first_name_required');
    if (!formData.lastName.trim()) newErrors.lastName = t('users.errors.last_name_required');
    if (!formData.email.trim()) {
      newErrors.email = t('users.errors.email_required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('users.errors.email_invalid');
    }
    if (!user) {
      if (!formData.password) {
        newErrors.password = t('users.errors.password_required');
      } else if (formData.password.length < 6) {
        newErrors.password = t('users.errors.password_min_length');
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = t('users.errors.password_mismatch');
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const userData = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim().toLowerCase(),
      role: formData.role,
      status: formData.status,
      permissions: formData.permissions,
      companyId,
    };
    if (!user && formData.password) userData.password = formData.password;
    onSave(userData);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handlePermissionChange = (permission, value) => {
    setFormData(prev => ({
      ...prev,
      permissions: { ...prev.permissions, [permission]: value },
    }));
  };

  const permissionLabel = (key) => {
    const map = {
      dashboard: t('navigation.dashboard'),
      shipments: t('navigation.shipments'),
      clients: t('navigation.clients'),
      drivers: t('navigation.drivers'),
      vehicles: t('navigation.vehicles'),
      accounting: t('navigation.accounting'),
      quotation: t('accounting.quotations', 'Quotations'),
      vendors: t('vendors.title', 'Vendors'),
      users: t('navigation.users'),
      settings: t('navigation.settings'),
    };
    return map[key] || key;
  };

  if (!isOpen) return null;

  const inputClass = (hasError) =>
    `block w-full px-3 py-2 border ${hasError ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-500`;
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50"
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold text-gray-900">
            {user ? t('users.edit_user') : t('users.add_user')}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            disabled={loading}
            type="button"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-6">
          {/* Personal info section */}
          <section>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              {t('users.personal_info', 'Personal Information')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>
                  {t('users.first_name')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className={inputClass(errors.firstName)}
                  disabled={loading}
                  placeholder={t('users.first_name')}
                />
                {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
              </div>
              <div>
                <label className={labelClass}>
                  {t('users.last_name')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className={inputClass(errors.lastName)}
                  disabled={loading}
                  placeholder={t('users.last_name')}
                />
                {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
              </div>
            </div>

            <div className="mt-4">
              <label className={labelClass}>
                {t('users.email')} <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={inputClass(errors.email)}
                disabled={loading || !!user}
                placeholder="user@example.com"
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>
          </section>

          {/* Password */}
          {!user && (
            <section>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                {t('users.password_section', 'Password')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>
                    {t('users.password')} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      className={`${inputClass(errors.password)} pr-10`}
                      disabled={loading}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                </div>
                <div>
                  <label className={labelClass}>
                    {t('users.confirm_password')} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      className={`${inputClass(errors.confirmPassword)} pr-10`}
                      disabled={loading}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
                </div>
              </div>
            </section>
          )}

          {/* Role & Status */}
          <section>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              {t('users.access_level', 'Access Level')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>
                  {t('users.role')} <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => { setRoleTouched(true); handleInputChange('role', e.target.value); }}
                  className={inputClass(false)}
                  disabled={loading}
                >
                  <option value="operations">{t('users.operations')}</option>
                  <option value="accounting">{t('users.accounting')}</option>
                  <option value="admin">{t('users.admin')}</option>
                  <option value="client">{t('users.client')}</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>
                  {t('users.status')} <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className={inputClass(false)}
                  disabled={loading}
                >
                  <option value="active">{t('users.active')}</option>
                  <option value="inactive">{t('users.inactive')}</option>
                </select>
              </div>
            </div>
          </section>

          {/* Permissions */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900">
                {t('users.permissions')}
              </h3>
              <p className="text-xs text-gray-500">
                {t('users.permissions_hint', 'Toggle which sections this user can access')}
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 rounded-md border border-gray-200 bg-gray-50">
              {PERMISSION_ORDER.map((permission) => {
                const enabled = !!formData.permissions[permission];
                const isAdmin = formData.role === 'admin';
                return (
                  <label
                    key={permission}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md bg-white border ${
                      enabled || isAdmin ? 'border-primary-300' : 'border-gray-200'
                    } ${isAdmin ? 'opacity-75' : 'cursor-pointer hover:border-gray-300'}`}
                  >
                    <input
                      type="checkbox"
                      checked={isAdmin ? true : enabled}
                      onChange={(e) => handlePermissionChange(permission, e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      disabled={loading || isAdmin}
                    />
                    <span className="text-sm text-gray-700 select-none">
                      {permissionLabel(permission)}
                    </span>
                  </label>
                );
              })}
            </div>
            {formData.role === 'admin' && (
              <p className="text-xs text-gray-500 mt-2">
                {t('users.admin_permissions_note')}
              </p>
            )}
          </section>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50 inline-flex items-center gap-2"
              disabled={loading}
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>{user ? t('common.updating') : t('common.adding')}</span>
                </>
              ) : (
                user ? t('common.update') : t('common.add')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserModal;
